// Safety — Incidents & Injuries API
// Thin service layer over the shared `api` util.

import api from './api';
import type { Attachment } from '../types/index';
import type {
    Incident,
    IncidentStatus,
    Certificate,
    Appointment,
    Expense,
    WageRecord,
    FileNote,
    RtwPlan,
    IncidentInvestigation,
    SafetyLists,
    LetterTemplate,
    EmployeeRef,
    EntityRef,
} from '../types/incident.types';

const BASE = '/api/safety/incidents';

export type IncidentAttachmentCategory = 'photos' | 'incidentReports' | 'medicalDocuments';

/* -------------------------------------------------------------------------- */
/*  Incidents CRUD                                                            */
/* -------------------------------------------------------------------------- */

export interface IncidentListParams {
    status?: string;
    search?: string;
    capacity?: string;
    entity?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    page?: number;
    limit?: number;
}

// Server-side filter/sort/pagination. Omitting `limit` returns all matching
// rows (used by the register export).
export async function listIncidents(params: IncidentListParams = {}): Promise<{ incidents: Incident[]; total: number }> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') query.append(k, String(v));
    });
    const qs = query.toString();
    const res = await api.get(qs ? `${BASE}?${qs}` : BASE);
    const incidents: Incident[] = res.incidents ?? [];
    return { incidents, total: res.total ?? incidents.length };
}

// Register alert-strip counts (open incidents, expiring certificates)
export async function getIncidentStats(): Promise<{ open: number; expiringCertificates: number }> {
    const res = await api.get(`${BASE}/stats`);
    return res.stats ?? res;
}

// Bulk status change for the register selection. Per-incident transition
// rules apply; failures are reported per id.
export async function bulkUpdateStatus(
    ids: number[],
    status: IncidentStatus,
): Promise<{ updated: number; failed: { id: number; message: string }[] }> {
    const res = await api.post(`${BASE}/bulk-status`, { ids, status });
    return { updated: res.updated ?? 0, failed: res.failed ?? [] };
}

export async function getIncident(id: number): Promise<Incident> {
    const res = await api.get(`${BASE}/${id}`);
    return res.incident ?? res;
}

export async function createIncident(payload: Partial<Incident>): Promise<Incident> {
    const res = await api.post(BASE, payload);
    return res.incident ?? res;
}

export async function updateIncident(id: number, payload: Partial<Incident>): Promise<Incident> {
    const res = await api.put(`${BASE}/${id}`, payload);
    return res.incident ?? res;
}

// Status changes go through a dedicated endpoint — the backend enforces the
// allowed transition map (Open ↔ RTW Plan → Closed; Closed → Open reopens).
export async function updateIncidentStatus(id: number, status: IncidentStatus): Promise<Incident> {
    const res = await api.put(`${BASE}/${id}/status`, { status });
    return res.incident ?? res;
}

/* -------------------------------------------------------------------------- */
/*  Attachments (S3)                                                          */
/* -------------------------------------------------------------------------- */

export async function uploadIncidentAttachments(
    incidentId: number,
    category: IncidentAttachmentCategory,
    files: File[],
): Promise<{ attachments: Attachment[]; errors: Array<{ filename: string; error?: string; errors?: string[] }> }> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    const res = await api.post(`${BASE}/${incidentId}/attachments?category=${category}`, form);
    return { attachments: res.attachments ?? [], errors: res.errors ?? [] };
}

/* -------------------------------------------------------------------------- */
/*  Sub-resources — each nested under an incident                             */
/* -------------------------------------------------------------------------- */

// Certificates
export async function addCertificate(incidentId: number, cert: Certificate): Promise<Incident> {
    const res = await api.post(`${BASE}/${incidentId}/certificates`, cert);
    return res.incident ?? res;
}

export async function deleteCertificate(incidentId: number, certId: string): Promise<Incident> {
    const res = await api.delete(`${BASE}/${incidentId}/certificates/${certId}`);
    return res.incident ?? res;
}

// Appointments
export async function addAppointment(incidentId: number, appt: Appointment): Promise<Incident> {
    const res = await api.post(`${BASE}/${incidentId}/appointments`, appt);
    return res.incident ?? res;
}

export async function deleteAppointment(incidentId: number, apptId: string): Promise<Incident> {
    const res = await api.delete(`${BASE}/${incidentId}/appointments/${apptId}`);
    return res.incident ?? res;
}

// Expenses
export async function addExpense(incidentId: number, expense: Expense): Promise<Incident> {
    const res = await api.post(`${BASE}/${incidentId}/expenses`, expense);
    return res.incident ?? res;
}

export async function deleteExpense(incidentId: number, expenseId: string): Promise<Incident> {
    const res = await api.delete(`${BASE}/${incidentId}/expenses/${expenseId}`);
    return res.incident ?? res;
}

// Wages
export async function addWage(incidentId: number, wage: WageRecord): Promise<Incident> {
    const res = await api.post(`${BASE}/${incidentId}/wages`, wage);
    return res.incident ?? res;
}

export async function deleteWage(incidentId: number, wageId: string): Promise<Incident> {
    const res = await api.delete(`${BASE}/${incidentId}/wages/${wageId}`);
    return res.incident ?? res;
}

// File notes
export async function addNote(incidentId: number, note: FileNote): Promise<Incident> {
    const res = await api.post(`${BASE}/${incidentId}/notes`, note);
    return res.incident ?? res;
}

export async function deleteNote(incidentId: number, noteId: string): Promise<Incident> {
    const res = await api.delete(`${BASE}/${incidentId}/notes/${noteId}`);
    return res.incident ?? res;
}

// RTW plan
export async function saveRtwPlan(incidentId: number, rtwPlan: RtwPlan): Promise<Incident> {
    const res = await api.put(`${BASE}/${incidentId}/rtw-plan`, rtwPlan);
    return res.incident ?? res;
}

// Investigation — whole-document replace (notes log, breached procedures,
// risk-assessment flag, corrective actions), same pattern as saveRtwPlan.
export async function saveInvestigation(incidentId: number, investigation: IncidentInvestigation): Promise<Incident> {
    const res = await api.put(`${BASE}/${incidentId}/investigation`, investigation);
    return res.incident ?? res;
}

// Workflow — set a step's completion state (done:false un-marks, e.g. on reopen).
// label/auto come from the client template so the backend can append sparse steps.
export async function setWorkflowStep(
    incidentId: number,
    step: number,
    payload: { done: boolean; label?: string; auto?: boolean },
): Promise<Incident> {
    const res = await api.put(`${BASE}/${incidentId}/workflow/${step}`, payload);
    return res.incident ?? res;
}

// Letters — emails the (already merged) letter to the incident's linked employee
export async function sendLetter(incidentId: number, payload: { subject: string; body: string }): Promise<{ message?: string }> {
    const res = await api.post(`${BASE}/${incidentId}/letters/send`, payload);
    return res;
}

/* -------------------------------------------------------------------------- */
/*  Settings — dropdown lists + letter templates                              */
/* -------------------------------------------------------------------------- */

export async function getSettings(): Promise<{ lists: SafetyLists; letterTemplates: LetterTemplate[] }> {
    const res = await api.get(`${BASE}/settings`);
    return res.settings ?? res;
}

export async function saveSettings(
    payload: Partial<{ lists: SafetyLists; letterTemplates: LetterTemplate[] }>,
): Promise<{ lists: SafetyLists; letterTemplates: LetterTemplate[] }> {
    const res = await api.put(`${BASE}/settings`, payload);
    return res.settings ?? res;
}

/* -------------------------------------------------------------------------- */
/*  Employer Entities — managed in Incidents & Injuries → Settings            */
/* -------------------------------------------------------------------------- */

export async function listEmployerEntities(): Promise<EntityRef[]> {
    const res = await api.get(`${BASE}/settings/employer-entities`);
    const rows = Array.isArray(res) ? res : res?.employerEntities ?? res?.data ?? [];
    return (rows as any[]).map((e) => ({ id: String(e.id), name: e.name }));
}

export async function createEmployerEntity(name: string): Promise<EntityRef> {
    const res = await api.post(`${BASE}/settings/employer-entities`, { name });
    const e = res.employerEntity ?? res;
    return { id: String(e.id), name: e.name };
}

export async function updateEmployerEntity(id: string, name: string): Promise<EntityRef> {
    const res = await api.put(`${BASE}/settings/employer-entities/${id}`, { name });
    const e = res.employerEntity ?? res;
    return { id: String(e.id), name: e.name };
}

export async function deleteEmployerEntity(id: string): Promise<void> {
    await api.delete(`${BASE}/settings/employer-entities/${id}`);
}

/* -------------------------------------------------------------------------- */
/*  Submission Notifications — Incidents & Injuries → Settings → Notifications */
/* -------------------------------------------------------------------------- */

export interface IncidentNotificationUser {
    id: number;
    name: string;
    email: string;
    department: string;
    userRole: string;
    isNotified: boolean;
}

export async function listNotificationUsers(
    params: { page?: number; limit?: number; search?: string } = {},
): Promise<{ users: IncidentNotificationUser[]; totalCount: number; totalPages: number }> {
    const query = new URLSearchParams();
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.search) query.append('search', params.search);
    const res = await api.get(`${BASE}/settings/notification-users?${query.toString()}`);
    const data = res.data ?? {};
    return {
        users: data.users ?? [],
        totalCount: data.totalCount ?? 0,
        totalPages: data.totalPages ?? 0,
    };
}

// Toggles a user's subscription to incident-submission emails.
// Returns the new subscribed state.
export async function toggleNotificationUser(userId: number): Promise<boolean> {
    const res = await api.patch(`${BASE}/settings/notifications/toggle`, { targetUserId: userId });
    return !!res.isNotified;
}

/* -------------------------------------------------------------------------- */
/*  Reference data                                                            */
/* -------------------------------------------------------------------------- */

export async function getEmployees(): Promise<EmployeeRef[]> {
    const res = await api.get('/api/users');
    const rows = Array.isArray(res) ? res : res?.users ?? res?.data ?? [];
    return (rows as any[]).map((u) => ({
        id: String(u.id),
        name: u.name ?? u.fullName ?? u.email,
    }));
}

// Employer entities shown on the Incident Report form come from the
// Incidents & Injuries settings (see listEmployerEntities).
export async function getEntities(): Promise<EntityRef[]> {
    return listEmployerEntities();
}
